/**
 * Easify cart bridge
 * ------------------
 * The Easify Product Options app intercepts the product form submit and performs
 * its OWN XHR POST to /cart/add.js (see product-options-*.js in the console trace).
 * Because the theme's <product-form-component> never runs its own fetch, it never
 * dispatches CartLinesUpdateEvent -- so the cart drawer never opens and the cart
 * icon count never updates until the shopper reloads the page.
 *
 * This bridge watches for any successful POST to /cart/add(.js) that the theme did
 * NOT make itself, then re-dispatches the standard CartLinesUpdateEvent so the
 * theme's existing cart-drawer / cart-icon / cart-items components update normally.
 *
 * It is additive only: it does not modify the theme's own add-to-cart path, and it
 * suppresses itself whenever the theme has already handled the add.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SUPPRESS_WINDOW_MS = 2000;
const ADD_URL_PATTERN = /\/cart\/add(\.js)?(\?|$)/;

/** Events this bridge dispatched itself, so they don't count as "theme handled". */
const ownEvents = new WeakSet();

/** Timestamp of the last cart update the theme dispatched on its own. */
let themeHandledAt = 0;

document.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    if (!ownEvents.has(event)) themeHandledAt = Date.now();
  },
  true
);

/**
 * @param {unknown} url
 * @returns {boolean}
 */
function isCartAddUrl(url) {
  if (typeof url !== 'string') return false;
  try {
    return ADD_URL_PATTERN.test(new URL(url, window.location.origin).pathname + (url.includes('?') ? '?' : ''));
  } catch (_) {
    return ADD_URL_PATTERN.test(url);
  }
}

/**
 * Re-dispatch a standard cart update so the theme's components refresh.
 * Deferred a tick so the theme gets first chance to handle its own add.
 */
function announceExternalAdd() {
  setTimeout(async () => {
    if (Date.now() - themeHandledAt < SUPPRESS_WINDOW_MS) return;

    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [],
      promise: deferred.promise,
    });

    ownEvents.add(event);
    document.dispatchEvent(event);

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

      const cart = await response.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: 'easify-cart-bridge',
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
    }
  }, 0);
}

// --- Watch XMLHttpRequest (this is what Easify uses) ---------------------------

const nativeOpen = XMLHttpRequest.prototype.open;
const nativeSend = XMLHttpRequest.prototype.send;

XMLHttpRequest.prototype.open = function (method, url, ...rest) {
  this.__cartBridgeMethod = method;
  this.__cartBridgeUrl = url;
  return nativeOpen.call(this, method, url, ...rest);
};

XMLHttpRequest.prototype.send = function (...args) {
  const isAdd =
    String(this.__cartBridgeMethod).toUpperCase() === 'POST' && isCartAddUrl(this.__cartBridgeUrl);

  if (isAdd) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) announceExternalAdd();
    });
  }

  return nativeSend.apply(this, args);
};

// --- Watch fetch too, in case the app switches transports ----------------------

const nativeFetch = window.fetch;

window.fetch = function (input, init, ...rest) {
  const url = typeof input === 'string' ? input : input?.url;
  const method = (init?.method ?? (typeof input === 'object' ? input?.method : null) ?? 'GET').toUpperCase();
  const isAdd = method === 'POST' && isCartAddUrl(url);

  const result = nativeFetch.call(this, input, init, ...rest);

  if (isAdd) {
    result
      .then((response) => {
        if (response.ok) announceExternalAdd();
        return response;
      })
      .catch(() => {});
  }

  return result;
};
